import { apiGet } from '@/lib/api';
import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import {
    ArrowLeft,
    Settings,
    Users,
    Plus,
    Loader2,
    CheckSquare,
    RefreshCw,
    Menu,
    LayoutGrid,
    Columns3,
    Calendar as CalendarIcon,
    BarChart3 as GanttIcon,
    FileText as DocumentIcon,
} from 'lucide-react';
import { useProject, useToggleFavorite } from '@/hooks/use-projects';
import {
    useTasks,
    useCreateTask,
    useDeleteTask,
    type TaskFilters,
} from '@/hooks/use-tasks';
import { useCustomFields, useDeleteCustomField } from '@/hooks/use-custom-fields';
import { useSavedViews, useUpdateSavedView, useCreateColumn } from '@/hooks/use-views';
import { useRealtimeTasks, useRealtimeCustomFields } from '@/hooks/use-realtime';
import { useAuthStore } from '@/stores/auth-store';
import { useI18n } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { TaskTable, TaskDialog, TaskFiltersBar, SavedViewsPanel, TaskDetailPanel, FieldFormDialog } from '@/components/tasks';
import { CustomFieldManager } from '@/components/tasks';
import { KanbanView } from '@/components/tasks/kanban';
import { CalendarView } from '@/components/tasks/calendar';
import { GanttView } from '@/components/tasks/gantt';
import { DocumentView } from '@/components/tasks/document';
import { AiHistoryDialog } from '@/components/ai/ai-history';
import { AIChat, AIConversationList } from '@/components/ai/ai-chat';
import { AddColumnDialog } from '@/components/tasks/kanban/add-column-dialog';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { apiDelete, apiPost, apiPatch, ApiClientError } from '@/lib/api';
import { AppSidebar } from '@/components/layout/app-sidebar';
import type { ProjectRole, Task, TaskStatus, TaskPriority, CreateTaskInput, UpdateTaskInput, SavedView, ViewConfig, CustomField, ViewType } from '@/lib/schemas';

// Local-storage key for the per-user, per-project view-type preference.
// Survives reloads without touching the database. Keyed on projectId so
// each project remembers its own choice. t15: now also covers the new
// CALENDAR + GANTT modes (used to be Table | Kanban only).
const viewTypeStorageKey = (projectId: string) => `taskflow:kanban:view-type:${projectId}`;
const aiChatStorageKey = (userId: string, projectId: string) => `taskflow:ai:chat:${userId}:${projectId}`;
function readStoredAiChat(userId: string | undefined, projectId: string | undefined) {
    if (!userId || !projectId) return null;
    try { return window.localStorage.getItem(aiChatStorageKey(userId, projectId)); } catch { return null; }
}

function readStoredViewType(projectId: string): ViewType {
    if (typeof window === 'undefined') return 'TABLE';
    const stored = window.localStorage.getItem(viewTypeStorageKey(projectId));
    if (
        stored === 'KANBAN' ||
        stored === 'CALENDAR' ||
        stored === 'GANTT' ||
        stored === 'DOCUMENT' ||
        stored === 'CHAT' ||
        stored === 'TABLE'
    ) {
        return stored;
    }
    return 'TABLE';
}

function writeStoredViewType(projectId: string, viewType: ViewType) {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(viewTypeStorageKey(projectId), viewType);
}

const roleLabels: Record<ProjectRole, string> = {
    OWNER: 'Owner',
    ADMIN: 'Admin',
    MEMBER: 'Member',
    VIEWER: 'Viewer',
};

const roleBadgeColors: Record<ProjectRole, string> = {
    OWNER: 'bg-amber-500/20 text-amber-400',
    ADMIN: 'bg-purple-500/20 text-purple-400',
    MEMBER: 'bg-blue-500/20 text-blue-400',
    VIEWER: 'bg-gray-500/20 text-gray-400',
};

export function ProjectDetailPage() {
    const { projectId } = useParams<{ projectId: string }>();
    const navigate = useNavigate();
    const { toast } = useToast();
    const { t, language } = useI18n();
    // R14: pass the current user's id + display name to the
    // DocumentView so the cursor presence has a label to broadcast.
    const authUser = useAuthStore((s) => s.user);

    // Mobile sidebar state
    const [sidebarOpen, setSidebarOpen] = useState(false);

    // Project data
    const { data: project, isLoading: projectLoading, error: projectError } = useProject(projectId || '');
    const toggleFavorite = useToggleFavorite();

    // Task state
    const [filters, setFilters] = useState<TaskFilters>({});
    const [taskDialogOpen, setTaskDialogOpen] = useState(false);
    const [editingTask, setEditingTask] = useState<Task | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [taskToDelete, setTaskToDelete] = useState<string | null>(null);
    // t18: when the Calendar view's right-click menu opens the create
    // dialog, the picked day lives here so TaskDialog can prefill
    // `dueDate`. Reset every time the dialog opens.
    const [taskDialogInitialDueDate, setTaskDialogInitialDueDate] = useState<string | null>(null);
    const [selectedTasks, setSelectedTasks] = useState<string[]>([]);
    const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
    // t10 fix: split the active-view state into an *id* (local UI
    // state — which view is selected) and a *derived object* (looked up
    // from the live savedViewsData query). The previous version copied
    // the server data into a frozen `activeView` useState on mount and
    // never refreshed it on cache invalidation, so KanbanView kept
    // rendering the stale snapshot even though the backend mutation had
    // succeeded. The derived activeView re-computes on every 
    // savedViewsData change, so column CRUD / create column / reorder
    // / popover toggles / card fields all flow through to the board
    // immediately without a manual refresh.
    const [activeViewId, setActiveViewId] = useState<string | null>(null);
    const [viewsInitialized, setViewsInitialized] = useState(false);
    const [columnOrder, setColumnOrder] = useState<string[]>([]);
    const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
    const [saveViewDialogOpen, setSaveViewDialogOpen] = useState(false);
    const [customFieldDialogOpen, setCustomFieldDialogOpen] = useState(false);
    const [editingCustomField, setEditingCustomField] = useState<CustomField | null>(null);
    const [deleteCustomFieldId, setDeleteCustomFieldId] = useState<string | null>(null);
    // t15 Part B.3: Add Column dialog opened after creating a KANBAN
    // view (or after the Kanban default seed). Lives at the page level
    // so SavedViewsPanel can drive it without owning the dialog itself.
    const [addColumnOpen, setAddColumnOpen] = useState(false);

    // Per-project view type (Table | Kanban). Lazy initialiser reads the
    // persisted choice from localStorage on first render so reload keeps
    // the user on whichever board they were looking at.
    const [aiConversationId, setAiConversationId] = useState<string | null>(() => readStoredAiChat(authUser?.id, projectId));
    const selectAiConversation = useCallback((id: string | null) => {
        setAiConversationId(id);
        if (authUser?.id && projectId) {
            try {
                if (id) localStorage.setItem(aiChatStorageKey(authUser.id, projectId), id);
                else localStorage.removeItem(aiChatStorageKey(authUser.id, projectId));
            } catch { /* Storage is optional; conversation access is checked by the API. */ }
        }
    }, [authUser?.id, projectId]);
    const [aiInitialPrompt, setAiInitialPrompt] = useState<{ nonce: number; text: string; documentIds?: string[] }>({ nonce: 0, text: '' });
    const [aiContext, setAiContext] = useState<Record<string, unknown>>({});
    const resourceLocation=useLocation();
    const [mcpActivityOpen, setMcpActivityOpen] = useState(() => !!new URLSearchParams(window.location.search).get('aiActivity'));
    useEffect(() => { setMcpActivityOpen(!!new URLSearchParams(resourceLocation.search).get('aiActivity')); }, [resourceLocation.search, projectId]);
    const [aiDocumentProposal, setAiDocumentProposal] = useState<Record<string, unknown> | null>(null);
    const [viewType, setViewType] = useState<ViewType>(() => readStoredViewType(projectId || ''));

    // When the user navigates to a different project, re-read the storage
    // so the toggle follows the project, not the global default.
    useEffect(() => {
        if (!projectId) return;
        const conversationFromUrl = new URLSearchParams(window.location.search).get('aiConversation');
        if (conversationFromUrl) { selectAiConversation(conversationFromUrl); setViewType('CHAT'); }
        else { const prompt = new URLSearchParams(location.search).get('aiPrompt'); if (prompt) { selectAiConversation(null); setViewType('CHAT'); setAiInitialPrompt({ nonce: Date.now(), text: prompt.slice(0, 20000) }); } else { setAiConversationId(readStoredAiChat(authUser?.id, projectId)); setViewType(readStoredViewType(projectId)); } }
    }, [projectId, authUser?.id, selectAiConversation]);


    const handleViewTypeChange = useCallback((next: ViewType) => {
        if (next === 'CHAT' && viewType !== 'CHAT') setAiContext({ viewType, viewId: activeViewId, selectedTaskIds: selectedTasks, filters, columnOrder, hiddenColumns });
        setViewType(next);
        if (projectId) writeStoredViewType(projectId, next);
        // Switching between Table and Kanban invalidates the currently
        // active view — the active Table view may not make sense as a
        // Kanban board and vice versa.
        setActiveViewId(null);
        setFilters({});
        setViewsInitialized(false);
    }, [projectId, viewType, activeViewId, selectedTasks, filters, columnOrder, hiddenColumns]);

    // t15 Part B.3: when the user creates a new KANBAN view via the
    // saved-views-panel + button, open the Add Column dialog so they can
    // immediately configure their first column with a single click.
    //
    // R2: also make the new view the active view across EVERY mode
    // (TABLE / CALENDAR / GANTT / KANBAN). Before this fix only
    // KANBAN triggered a state change; other modes left activeView
    // pointing at the previous view and the user had to click the
    // new row to see it.
    //
    // R4 (t19): the previous Kanban auto-open of `AddColumnDialog`
    // on view creation was removed. The user reported it was
    // annoying — when creating a custom view they want the empty
    // state with its "Créer la première colonne" CTA, not an
    // instant modal pop-up. The empty-state CTA still drives the
    // same `setAddColumnOpen(true)` flow.
    const handleViewCreated = useCallback(
        (view: SavedView) => {
            // Always make the freshly-created view the active one for
            // the current mode so it appears selected in the sidebar
            // and the board / calendar / gantt renders its config.
            //
            // R4 (t19): removed the KANBAN auto-open of AddColumnDialog.
            // The empty state now renders the
            // "Créer la première colonne" CTA, which the user
            // clicks explicitly to open the same dialog.
            setActiveViewId(view.id);
        },
        [setActiveViewId]
    );

    // Task queries and mutations
    const {
        data: tasksData,
        isLoading: tasksLoading,
        refetch: refetchTasks,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useTasks(projectId || '', filters);
    const latestTasksRef = useRef(tasksData?.tasks);

    useEffect(() => {
        latestTasksRef.current = tasksData?.tasks;
    }, [tasksData?.tasks]);
    const createTask = useCreateTask(projectId || '');
    const deleteTask = useDeleteTask(projectId || '');

    // Custom fields
    const { data: customFields = [] } = useCustomFields(projectId || '');
    const deleteCustomField = useDeleteCustomField(projectId || '');

    // Saved views - to load default view on page load. We fetch the
    // type-filtered list so the "default view" lookup matches whatever
    // the toggle is currently set to (a Table default is irrelevant when
    // the user is looking at the Kanban board, and vice versa).
    const { data: savedViewsData } = useSavedViews(projectId || '', viewType);
    useEffect(()=>{
        const query=new URLSearchParams(resourceLocation.search);
        const taskId=query.get('task'), viewId=query.get('view');
        if(taskId) setSelectedTaskId(taskId);
        if(viewId && savedViewsData?.views) {
            const view=savedViewsData.views.find(view=>view.id===viewId);
            if(view) {setViewType(view.type as ViewType);setActiveViewId(view.id);setViewsInitialized(true);setAiDocumentProposal((resourceLocation.state as {aiDocumentProposal?:Record<string,unknown>}|null)?.aiDocumentProposal ?? null);}
        }
    },[resourceLocation.search,resourceLocation.state,savedViewsData]);

    const updateSavedView = useUpdateSavedView(projectId || '');
    // Hook for the AddColumnDialog (Part B.3). Initialised lazily —
    // when activeView is null the hook still exists but the underlying
    // mutation can no-op.
    const createColumnHook = useCreateColumn(projectId || '', activeViewId ?? '', undefined);

    // R2: removed the three auto-seed "All tasks" useEffects (KANBAN
    // ~was 230-257, CALENDAR ~was 265-290, GANTT ~was 307-333). The
    // user spec says NO custom view should be auto-created on first
    // open of a mode. The sidebar's "All tasks" entry remains a UI
    // label for activeViewId === null; the downstream mode components
    // are updated by the per-mode engineers to render appropriate
    // defaults when no view is selected.

    // Load default view on first load. We only track the *id* locally
    // (activeViewId) and let activeView below be derived from the live
    // savedViewsData query so every cache invalidation triggers a
    // recompute automatically.
    useEffect(() => {
        if (savedViewsData?.views && !viewsInitialized) {
            const defaultView = savedViewsData.views.find((v) => v.isDefault);
            if (defaultView) {
                setActiveViewId(defaultView.id);
                if (defaultView.config?.filters) {
                    setFilters({
                        status: defaultView.config.filters.status as TaskStatus[] | undefined,
                        priority: defaultView.config.filters.priority as TaskPriority[] | undefined,
                        assignee: defaultView.config.filters.assignees?.[0],
                        dueDateFrom: defaultView.config.filters.dueDateFrom,
                        dueDateTo: defaultView.config.filters.dueDateTo,
                        search: defaultView.config.filters.search,
                        customFields: defaultView.config.filters.customFields,
                    });
                }
                if (defaultView.config?.columnOrder) {
                    setColumnOrder(defaultView.config.columnOrder);
                }
                if (defaultView.config?.hiddenColumns) {
                    setHiddenColumns(defaultView.config.hiddenColumns);
                }
            }
            setViewsInitialized(true);
        }
    }, [savedViewsData, viewsInitialized]);

    // R24 (Bug: Document mode showed a "General" sidebar entry):
    // DOCUMENT mode no longer renders the virtual default entry in the
    // sidebar — a document has no tasks, and the old entry was a
    // local-only scratchpad that never saved. So Document mode must
    // land on a REAL DOCUMENT saved view: whenever the mode is
    // DOCUMENT and the selection is null (first open, or right after
    // switching modes with `handleViewTypeChange`) or points at a view
    // that isn't a DOCUMENT view, auto-select the default DOCUMENT
    // view, else a favorite one, else the first in the list. With zero
    // DOCUMENT views we stay on null so DocumentView can show its
    // "create your first document" empty state. Other modes are
    // untouched — their "All tasks" entry still means
    // `activeViewId === null`.
    useEffect(() => {
        if (viewType !== 'DOCUMENT') return;
        // `undefined` = the type-filtered list is still loading;
        // `{ views: [] }` = loaded and genuinely empty.
        if (!savedViewsData?.views) return;
        const documentViews = savedViewsData.views.filter((v) => v.type === 'DOCUMENT');
        if (documentViews.length === 0) {
            // Nothing real to open — the empty state owns this case.
            if (activeViewId !== null) setActiveViewId(null);
            return;
        }
        // Selection already resolves to a live DOCUMENT view: keep it.
        if (activeViewId && documentViews.some((v) => v.id === activeViewId)) {
            return;
        }
        const preferred =
            documentViews.find((v) => v.isDefault) ??
            documentViews.find((v) => v.isFavorite) ??
            documentViews[0];
        if (preferred) setActiveViewId(preferred.id);
    }, [viewType, savedViewsData, activeViewId]);

    // Derived activeView — always reflects the latest savedViewsData.
    // This is the keystone of the t10 cache-invalidation fix.
    const activeView = useMemo(
        () => savedViewsData?.views?.find((v) => v.id === activeViewId) ?? null,
        [savedViewsData, activeViewId]
    );

    // Real-time task updates subscription
    useRealtimeTasks(projectId);

    // Real-time custom field updates subscription
    useRealtimeCustomFields(projectId);

    const getInitials = (name: string) => {
        return name
            .split(' ')
            .map((n) => n[0])
            .join('')
            .toUpperCase()
            .slice(0, 2);
    };

    const canManage = project?.role === 'OWNER' || project?.role === 'ADMIN';
    const canEditTasks = project?.role !== 'VIEWER';

    // Compute filter defaults for task creation
    const filterDefaults = useMemo(() => ({
        status: filters.status?.[0],
        priority: filters.priority?.[0],
        assigneeId: filters.assignee,
        customFields: filters.customFields,
    }), [filters]);

    // Combine owner and members for task assignment
    const allMembers = useMemo(() => {
        if (!project) return [];
        return [
            {
                userId: project.owner.id,
                user: project.owner,
            },
            ...project.members.map((m) => ({
                userId: m.userId,
                user: m.user,
            })),
        ];
    }, [project]);

    // Handle view selection - track the id locally and restore filters +
    // column state. The activeView *object* is derived elsewhere so we
    // never need to manually copy server data into React state.
    const handleViewSelect = useCallback((view: SavedView | null) => {
        setActiveViewId(view?.id ?? null);
        if (view?.config?.filters) {
            setFilters({
                status: view.config.filters.status as TaskStatus[] | undefined,
                priority: view.config.filters.priority as TaskPriority[] | undefined,
                assignee: view.config.filters.assignees?.[0],
                dueDateFrom: view.config.filters.dueDateFrom,
                dueDateTo: view.config.filters.dueDateTo,
                search: view.config.filters.search,
                customFields: view.config.filters.customFields,
            });
        } else {
            setFilters({});
        }
        // Restore column state
        if (view?.config?.columnOrder) {
            setColumnOrder(view.config.columnOrder);
        } else {
            setColumnOrder([]);
        }
        if (view?.config?.hiddenColumns) {
            setHiddenColumns(view.config.hiddenColumns);
        } else {
            setHiddenColumns([]);
        }
    }, []);

    // Current view config for saving - include custom fields and column state.
    // t43 (R13): customFields may carry the new per-type shape — we cast
    // through `unknown` because the source-of-truth type is in
    // hooks/use-tasks.ts and includes both legacy + new shapes.
    const currentViewConfig = useMemo((): ViewConfig => ({
        filters: {
            status: filters.status,
            priority: filters.priority,
            assignees: filters.assignee ? [filters.assignee] : undefined,
            dueDateFrom: filters.dueDateFrom,
            dueDateTo: filters.dueDateTo,
            search: filters.search,
            customFields: filters.customFields as
                | Record<string, string[] | {
                    type: 'TEXT' | 'NUMBER' | 'DATE' | 'SELECT' | 'MULTI_SELECT' | 'CHECKBOX' | 'URL' | 'EMAIL' | 'PERSON' | 'SPRINT';
                    op: string;
                    value?: unknown;
                    value2?: unknown;
                }>
                | undefined,
        },
        columnOrder: columnOrder.length > 0 ? columnOrder : undefined,
        hiddenColumns: hiddenColumns.length > 0 ? hiddenColumns : undefined,
    }), [filters, columnOrder, hiddenColumns]);

    // Handle updating the current active view with current config.
    // We DON'T manually merge currentViewConfig into local state any more:
    // the PATCH mutates the row, the useSavedViews query is invalidated
    // by invalidateAllViewsForProject in use-views.ts, and the derived
    // activeView above picks up the new config on the next render.
    //
    // t12 fix: the backend's PATCH /views/:id replaces `config` wholesale
    // (it doesn't deep-merge), so sending only `{ config: currentViewConfig }`
    // would wipe the view's `kanbanColumns`. We now merge currentViewConfig
    // into the existing config before sending — preserving the columns.
    const handleUpdateView = useCallback(async () => {
        if (!activeView) return;

        try {
            const mergedConfig = {
                ...(activeView.config || {}),
                ...currentViewConfig,
            };
            await updateSavedView.mutateAsync({
                viewId: activeView.id,
                data: {
                    config: mergedConfig,
                },
            });
            toast({
                title: t('toast.viewSaved'),
                description: t('toast.viewSavedDesc'),
            });
        } catch (error) {
            toast({
                title: t('common.error'),
                description: t('toast.errorSavingView'),
                variant: 'destructive',
            });
        }
    }, [activeView, currentViewConfig, updateSavedView, toast, t]);

    // Task handlers
    const handleCreateTask = useCallback(() => {
        setEditingTask(null);
        setTaskDialogInitialDueDate(null);
        setTaskDialogOpen(true);
    }, []);

    // t18: Calendar view's right-click "Create task at <date>" — open
    // the task creation dialog with the due date pre-filled.
    const handleCreateTaskAtDate = useCallback((dateIso: string) => {
        setEditingTask(null);
        setTaskDialogInitialDueDate(dateIso);
        setTaskDialogOpen(true);
    }, []);

    const handleEditTask = useCallback((task: Task) => {
        setEditingTask(task);
        setTaskDialogInitialDueDate(null);
        setTaskDialogOpen(true);
    }, []);

    const handleTaskClick = useCallback((task: Task) => {
        setSelectedTaskId(task.id);
    }, []);

    const handleCloseTaskDetail = useCallback(() => {
        setSelectedTaskId(null);
    }, []);

    const handleDeleteTask = useCallback((taskId: string) => {
        setTaskToDelete(taskId);
        setDeleteDialogOpen(true);
    }, []);

    // Inline task creation handler - auto-apply filter values so task stays visible in current view
    const handleInlineCreate = useCallback(async (title: string, parentId?: string) => {
        // Start with defaults
        let status: TaskStatus = 'TODO';
        let priority: TaskPriority = 'NONE';
        let assigneeIds: string[] = [];
        let customFieldValues: Record<string, any> = {};

        // Only apply filter values for root tasks (not subtasks)
        if (!parentId) {
            // Apply filter values so the new task matches the current view
            if (filters.status && filters.status.length === 1 && filters.status[0]) {
                status = filters.status[0] as TaskStatus;
            }
            if (filters.priority && filters.priority.length === 1 && filters.priority[0]) {
                priority = filters.priority[0] as TaskPriority;
            }
            if (filters.assignee) {
                assigneeIds = [filters.assignee];
            }
            // Apply custom field filter values (t43 R13: legacy
            // option-id-list shape only; the new per-type filter
            // shape is applied server-side at query time so this
            // block only seeds default values for SELECT/MULTI_SELECT
            // filters that the user just clicked).
            if (filters.customFields) {
                for (const [fieldId, raw] of Object.entries(filters.customFields)) {
                    const optionIds = Array.isArray(raw)
                        ? (raw as string[])
                        : null;
                    if (optionIds && optionIds.length > 0) {
                        // Find the field to check if it's multi-select or single select
                        const field = customFields.find(f => f.id === fieldId);
                        if (field?.type === 'MULTI_SELECT') {
                            customFieldValues[fieldId] = optionIds;
                        } else if (field?.type === 'SELECT' && optionIds.length === 1) {
                            customFieldValues[fieldId] = optionIds[0];
                        }
                    }
                }
            }
        }

        // Use subtask endpoint if parentId is provided
        if (parentId) {
            const response = await fetch(
                `/api/tasks/${parentId}/subtasks`,
                {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        title,
                        status,
                        priority,
                        assigneeIds,
                        customFieldValues,
                    }),
                }
            );
            if (!response.ok) throw new Error('Failed to create subtask');
            toast({
                title: 'Subtask created',
                description: 'New subtask has been added.',
            });
        } else {
            await createTask.mutateAsync({
                title,
                status,
                priority,
                assigneeIds,
                customFieldValues,
            });
            toast({
                title: t('toast.taskCreated'),
                description: t('toast.taskCreatedDesc'),
            });
        }
        refetchTasks();
    }, [createTask, toast, refetchTasks, filters, customFields, t]);

    // Quick create with all fields (status, priority, due date, assignees, custom fields)
    const handleQuickCreate = useCallback(async (data: {
        title: string;
        status: TaskStatus;
        priority: TaskPriority;
        dueDate: string | null;
        assigneeIds: string[];
        parentId?: string;
        customFieldValues?: Record<string, unknown>;
    }) => {
        // Use subtask endpoint if parentId is provided
        if (data.parentId) {
            const response = await fetch(
                `/api/tasks/${data.parentId}/subtasks`,
                {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        title: data.title,
                        status: data.status,
                        priority: data.priority,
                        dueDate: data.dueDate,
                        assigneeIds: data.assigneeIds,
                        customFieldValues: data.customFieldValues || {},
                    }),
                }
            );
            if (!response.ok) throw new Error('Failed to create subtask');
            toast({
                title: t('toast.subtaskCreated'),
                description: t('toast.subtaskCreatedDesc'),
            });
        } else {
            await createTask.mutateAsync({
                title: data.title,
                status: data.status,
                priority: data.priority,
                dueDate: data.dueDate || undefined,
                assigneeIds: data.assigneeIds,
                customFieldValues: data.customFieldValues || {},
            });
            toast({
                title: t('toast.taskCreated'),
                description: t('toast.taskCreatedDesc'),
            });
        }
        refetchTasks();
    }, [createTask, toast, refetchTasks, t]);

    // Handle inline title change
    const handleTitleChange = useCallback(async (taskId: string, title: string) => {
        const response = await fetch(
            `/api/tasks/${taskId}`,
            {
                method: 'PATCH',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ title }),
            }
        );
        if (!response.ok) throw new Error('Failed to update task title');
        refetchTasks();
    }, [refetchTasks]);

    // Bulk delete handler
    const handleBulkDelete = useCallback(async (taskIds: string[]) => {
        try {
            for (const taskId of taskIds) {
                await deleteTask.mutateAsync(taskId);
            }
            toast({
                title: t('toast.tasksDeleted'),
                description: t('toast.tasksDeletedDesc').replace('{count}', String(taskIds.length)),
            });
            setSelectedTasks([]);
            refetchTasks();
        } catch (error) {
            if (error instanceof ApiClientError && error.statusCode === 401) return;
            toast({
                title: t('common.error'),
                description: error instanceof Error ? error.message : t('toast.errorDeletingTasks'),
                variant: 'destructive',
            });
        }
    }, [deleteTask, toast, refetchTasks, t]);

    // Bulk status change handler
    const handleBulkStatusChange = useCallback(async (taskIds: string[], status: TaskStatus) => {
        try {
            for (const taskId of taskIds) {
                await fetch(
                    `/api/tasks/${taskId}`,
                    {
                        method: 'PATCH',
                        credentials: 'include',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ status }),
                    }
                );
            }
            toast({
                title: t('toast.statusUpdated'),
                description: t('toast.statusUpdatedDesc').replace('{count}', String(taskIds.length)),
            });
            refetchTasks();
        } catch (error) {
            toast({
                title: t('common.error'),
                description: t('toast.errorUpdatingTasks'),
                variant: 'destructive',
            });
        }
    }, [toast, refetchTasks, t]);

    // Bulk priority change handler
    const handleBulkPriorityChange = useCallback(async (taskIds: string[], priority: TaskPriority) => {
        try {
            for (const taskId of taskIds) {
                await fetch(
                    `/api/tasks/${taskId}`,
                    {
                        method: 'PATCH',
                        credentials: 'include',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ priority }),
                    }
                );
            }
            toast({
                title: t('toast.priorityUpdated'),
                description: t('toast.priorityUpdatedDesc').replace('{count}', String(taskIds.length)),
            });
            refetchTasks();
        } catch (error) {
            toast({
                title: t('common.error'),
                description: t('toast.errorUpdatingTasks'),
                variant: 'destructive',
            });
        }
    }, [toast, refetchTasks, t]);

    // Bulk assignee change handler
    const handleBulkAssigneeChange = useCallback(async (taskIds: string[], assigneeIds: string[]) => {
        try {
            for (const taskId of taskIds) {
                // Get current task assignees
                const task = latestTasksRef.current?.find(t => t.id === taskId);
                const currentAssignees = task?.assignees.map(a => a.userId) || [];

                // Find assignees to add
                const toAdd = assigneeIds.filter(id => !currentAssignees.includes(id));

                // Add new assignees
                for (const userId of toAdd) {
                    await fetch(
                        `/api/tasks/${taskId}/assignees`,
                        {
                            method: 'POST',
                            credentials: 'include',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ userId }),
                        }
                    );
                }
            }
            toast({
                title: t('toast.assigneesUpdated'),
                description: t('toast.assigneesUpdatedDesc').replace('{count}', String(taskIds.length)),
            });
            refetchTasks();
        } catch (error) {
            toast({
                title: t('common.error'),
                description: t('toast.errorUpdatingTasks'),
                variant: 'destructive',
            });
        }
    }, [toast, refetchTasks, tasksData?.tasks, t]);

    // Bulk custom field change handler
    const handleBulkCustomFieldChange = useCallback(async (taskIds: string[], fieldId: string, value: unknown) => {
        try {
            for (const taskId of taskIds) {
                const task = tasksData?.tasks.find(t => t.id === taskId);
                const currentValues = task?.customFieldValues || {};

                let newValue = value;

                // Handle MULTI_SELECT toggle operation
                if (value && typeof value === 'object' && 'toggleOption' in value) {
                    const toggleOptionId = (value as { toggleOption: string }).toggleOption;
                    const currentFieldValue = (currentValues[fieldId] as string[] | undefined) || [];

                    if (currentFieldValue.includes(toggleOptionId)) {
                        // Remove the option
                        newValue = currentFieldValue.filter(id => id !== toggleOptionId);
                    } else {
                        // Add the option
                        newValue = [...currentFieldValue, toggleOptionId];
                    }
                }

                const newValues = { ...currentValues, [fieldId]: newValue };

                await fetch(
                    `/api/tasks/${taskId}`,
                    {
                        method: 'PATCH',
                        credentials: 'include',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ customFieldValues: newValues }),
                    }
                );
            }
            toast({
                title: t('toast.taskUpdated'),
                description: t('toast.statusUpdatedDesc').replace('{count}', String(taskIds.length)),
            });
            refetchTasks();
        } catch (error) {
            toast({
                title: t('common.error'),
                description: t('toast.errorUpdatingTasks'),
                variant: 'destructive',
            });
        }
    }, [toast, refetchTasks, tasksData?.tasks]);

    const confirmDeleteTask = useCallback(async () => {
        if (!taskToDelete) return;
        try {
            await deleteTask.mutateAsync(taskToDelete);
            toast({
                title: t('toast.taskDeleted'),
                description: t('toast.taskDeletedDesc'),
            });
            setDeleteDialogOpen(false);
            setTaskToDelete(null);
        } catch (error) {
            if (error instanceof ApiClientError && error.statusCode === 401) return;
            toast({
                title: t('common.error'),
                description: error instanceof Error
                    ? error.message
                    : t('toast.errorTaskAction').replace('{action}', 'delete'),
                variant: 'destructive',
            });
        }
    }, [taskToDelete, deleteTask, toast, t]);

    const handleTaskSubmit = useCallback(
        async (data: CreateTaskInput | UpdateTaskInput) => {
            try {
                if (editingTask) {
                    // Update existing task
                    const response = await fetch(
                        `/api/tasks/${editingTask.id}`,
                        {
                            method: 'PATCH',
                            credentials: 'include',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify(data),
                        }
                    );
                    if (!response.ok) throw new Error('Failed to update task');
                    toast({
                        title: t('toast.taskUpdated'),
                        description: t('toast.taskUpdatedDesc'),
                    });
                } else {
                    // Create new task
                    await createTask.mutateAsync(data as CreateTaskInput);
                    toast({
                        title: t('toast.taskCreated'),
                        description: t('toast.taskCreatedDesc'),
                    });
                }
                setTaskDialogOpen(false);
                setEditingTask(null);
                setTaskDialogInitialDueDate(null);
                refetchTasks();
            } catch (error) {
                toast({
                    title: t('common.error'),
                    description: t('toast.errorTaskAction').replace('{action}', editingTask ? 'update' : 'create'),
                    variant: 'destructive',
                });
            }
        },
        [editingTask, createTask, toast, refetchTasks, t]
    );

    const handleStatusChange = useCallback(
        async (taskId: string, status: TaskStatus) => {
            try {
                const response = await fetch(
                    `/api/tasks/${taskId}`,
                    {
                        method: 'PATCH',
                        credentials: 'include',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ status }),
                    }
                );
                if (!response.ok) throw new Error('Failed to update status');
                refetchTasks();
                toast({
                    title: t('toast.statusUpdated'),
                    description: t('toast.statusUpdatedDesc').replace('{count}', '1'),
                });
            } catch (error) {
                toast({
                    title: t('common.error'),
                    description: t('toast.errorUpdatingTasks'),
                    variant: 'destructive',
                });
            }
        },
        [refetchTasks, toast, t]
    );

    // Drag-and-drop on the Kanban board reuses the same /api/tasks/:id
    // endpoint as the inline status change in the table. This keeps a
    // single source of truth for permission, completion-date handling,
    // and validation. The KanbanView applies the optimistic UI itself
    // and only invokes this callback for the actual network round-trip.
    //
    // t4+: a Kanban column can target any single task field (status,
    // priority, or a custom-field value) so the signature accepts the
    // generic {field, value} payload. Multi-rule columns fall back to
    // a toast before reaching this callback so we never have to handle
    // them here.
    const handleKanbanTaskMoved = useCallback(
        async (taskId: string, update: { field: 'status' | 'priority' | 'customFieldValues'; value: unknown }) => {
            const payload: Record<string, unknown> = {};
            if (update.field === 'customFieldValues') {
                payload.customFieldValues = update.value;
            } else {
                payload[update.field] = update.value;
            }
            const response = await apiPatch(`/tasks/${taskId}`, payload);
            if (!response) return;
            // Refetch so the server's view of completedAt / etc stays in
            // sync with what we just rendered optimistically.
            await refetchTasks();
        },
        [refetchTasks]
    );

    const handlePriorityChange = useCallback(
        async (taskId: string, priority: TaskPriority) => {
            try {
                const response = await fetch(
                    `/api/tasks/${taskId}`,
                    {
                        method: 'PATCH',
                        credentials: 'include',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ priority }),
                    }
                );
                if (!response.ok) throw new Error('Failed to update priority');
                refetchTasks();
                toast({
                    title: t('toast.priorityUpdated'),
                    description: t('toast.priorityUpdatedDesc').replace('{count}', '1'),
                });
            } catch (error) {
                toast({
                    title: t('common.error'),
                    description: t('toast.errorUpdatingTasks'),
                    variant: 'destructive',
                });
            }
        },
        [refetchTasks, toast, t]
    );

    // Handle inline assignee change
    const handleAssigneeChange = useCallback(
        async (taskId: string, assigneeIds: string[]) => {
            try {
                // Get current task assignees
                const task = latestTasksRef.current?.find(t => t.id === taskId);
                const currentAssignees = task?.assignees.map(a => a.userId) || [];

                // Find assignees to add and remove
                const toAdd = assigneeIds.filter(id => !currentAssignees.includes(id));
                const toRemove = currentAssignees.filter(id => !assigneeIds.includes(id));

                // Add new assignees
                for (const userId of toAdd) {
                    await apiPost(`/tasks/${taskId}/assignees`, { userId });
                }

                // Remove unselected assignees
                for (const userId of toRemove) {
                    await apiDelete(`/tasks/${taskId}/assignees/${encodeURIComponent(userId)}`);
                }

                await refetchTasks();
            } catch (error) {
                toast({
                    title: t('common.error'),
                    description: t('toast.errorAssignees'),
                    variant: 'destructive',
                });
            }
        },
        [refetchTasks, toast, t]
    );

    // Handle inline due date change
    const handleDueDateChange = useCallback(
        async (taskId: string, dueDate: string | null) => {
            try {
                const response = await fetch(
                    `/api/tasks/${taskId}`,
                    {
                        method: 'PATCH',
                        credentials: 'include',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ dueDate }),
                    }
                );
                if (!response.ok) throw new Error('Failed to update due date');
                refetchTasks();
                toast({
                    title: t('toast.dueDateUpdated'),
                    description: dueDate ? t('toast.dueDateSet') : t('toast.dueDateCleared'),
                });
            } catch (error) {
                toast({
                    title: t('common.error'),
                    description: t('toast.errorDueDate'),
                    variant: 'destructive',
                });
            }
        },
        [refetchTasks, toast, t]
    );

    // Handle inline custom field change
    const handleCustomFieldChange = useCallback(
        async (taskId: string, fieldId: string, value: unknown) => {
            try {
                const task = tasksData?.tasks.find(t => t.id === taskId);
                const currentValues = task?.customFieldValues || {};
                const newValues = { ...currentValues, [fieldId]: value };

                const response = await fetch(
                    `/api/tasks/${taskId}`,
                    {
                        method: 'PATCH',
                        credentials: 'include',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ customFieldValues: newValues }),
                    }
                );
                if (!response.ok) throw new Error('Failed to update custom field');
                refetchTasks();
            } catch (error) {
                toast({
                    title: t('common.error'),
                    description: t('toast.errorCustomField'),
                    variant: 'destructive',
                });
            }
        },
        [refetchTasks, toast, tasksData?.tasks, t]
    );

    // Handle edit custom field
    const handleEditCustomField = useCallback((field: CustomField) => {
        setEditingCustomField(field);
        setCustomFieldDialogOpen(true);
    }, []);

    // Handle delete custom field
    const handleDeleteCustomField = useCallback(async (fieldId: string) => {
        setDeleteCustomFieldId(fieldId);
    }, []);

    // Confirm delete custom field
    const confirmDeleteCustomField = useCallback(async () => {
        if (!deleteCustomFieldId) return;
        try {
            await deleteCustomField.mutateAsync(deleteCustomFieldId);
            toast({
                title: t('toast.fieldDeleted'),
                description: t('toast.fieldDeletedDesc'),
            });
            setDeleteCustomFieldId(null);
        } catch (error) {
            toast({
                title: t('common.error'),
                description: t('toast.errorDeletingField'),
                variant: 'destructive',
            });
        }
    }, [deleteCustomFieldId, deleteCustomField, toast, t]);

    if (projectLoading) {
        return (
            <div className="flex min-h-screen items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    if (projectError || !project) {
        return (
            <div className="flex min-h-screen items-center justify-center">
                <div className="text-center">
                    <h2 className="text-xl font-semibold text-foreground mb-2">
                        {t('projectSettings.projectNotFound')}
                    </h2>
                    <p className="text-foreground-muted mb-4">
                        {t('error.projectNotFoundDesc')}
                    </p>
                    <Button onClick={() => navigate('/projects')}>
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        {t('projectSettings.backToProjects')}
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="flex h-screen w-screen overflow-hidden fixed inset-0">
            {sidebarOpen && (
                <button
                    className="fixed inset-0 bg-black/50 z-40 md:hidden"
                    onClick={() => setSidebarOpen(false)}
                    aria-label={t('common.close')}
                />
            )}

            <AppSidebar
                open={sidebarOpen}
                onClose={() => setSidebarOpen(false)}
                activeProjectId={projectId}
            >
                <div className="pt-4 mt-4 border-t border-white/10">
                    <p className="px-3 text-xs font-medium text-white/50 uppercase tracking-wider mb-2">
                        {t('projects.currentProject')}
                    </p>
                    <div className="flex items-center gap-3 px-3 py-2 bg-white/10 rounded-lg">
                        {project.imageUrl ? (
                            <img src={project.imageUrl} alt="" className="h-8 w-8 rounded-lg object-cover" />
                        ) : (
                            <div
                                className="h-8 w-8 rounded-lg flex items-center justify-center text-lg"
                                style={{ backgroundColor: project.color || '#6366f1' }}
                            >
                                {project.iconEmoji || '📁'}
                            </div>
                        )}
                        <span className="font-medium text-white text-sm truncate">{project.name}</span>
                    </div>
                </div>

                <div className="pt-4 mt-4 border-t border-white/10">
                    {viewType === 'CHAT' ? <AIConversationList projectId={projectId || ''} activeId={aiConversationId} onSelect={selectAiConversation} canCreate={canEditTasks} /> : <SavedViewsPanel
                        projectId={projectId || ''}
                        activeViewId={activeView?.id}
                        currentConfig={currentViewConfig}
                        onViewSelect={handleViewSelect}
                        openSaveDialog={saveViewDialogOpen}
                        onSaveDialogChange={setSaveViewDialogOpen}
                        viewType={viewType}
                        customFields={customFields}
                        onViewCreated={handleViewCreated}
                    />}
                </div>
            </AppSidebar>

            {/* Main content */}
            <main className="flex-1 min-w-0 flex flex-col overflow-hidden h-full">
                {/* Project header */}
                <header className="border-b border-white/10 backdrop-blur-md bg-white/5 px-4 sm:px-6 py-4 shrink-0">
                    <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 sm:gap-4 min-w-0">
                            {/* Mobile menu button */}
                            <button
                                onClick={() => setSidebarOpen(true)}
                                className="md:hidden p-2 rounded-lg hover:bg-white/10 shrink-0"
                            >
                                <Menu className="h-5 w-5" />
                            </button>
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => navigate('/projects')}
                                className="shrink-0"
                            >
                                <ArrowLeft className="h-4 w-4" />
                            </Button>
                            <div className="flex items-center gap-3 min-w-0">
                                {project.imageUrl ? (
                                    <img
                                        src={project.imageUrl}
                                        alt={project.name}
                                        className="h-8 w-8 sm:h-10 sm:w-10 rounded-lg object-cover shrink-0"
                                    />
                                ) : (
                                    <div
                                        className="h-8 w-8 sm:h-10 sm:w-10 rounded-lg flex items-center justify-center text-lg sm:text-xl shrink-0"
                                        style={{ backgroundColor: project.color || '#6366f1' }}
                                    >
                                        {project.iconEmoji || '📁'}
                                    </div>
                                )}
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2">
                                        <h1 className="text-lg sm:text-xl font-semibold text-white truncate">
                                            {project.name}
                                        </h1>
                                        <span
                                            className={`hidden sm:inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${roleBadgeColors[project.role]
                                                }`}
                                        >
                                            {roleLabels[project.role]}
                                        </span>
                                    </div>
                                    {project.description && (
                                        <p className="text-sm text-white/50 truncate hidden sm:block">
                                            {project.description}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            {/* Members avatars - hidden on mobile */}
                            <div className="hidden sm:flex items-center -space-x-2 mr-2">
                                <Avatar className="h-8 w-8 border-2 border-surface">
                                    <AvatarImage src={project.owner.avatarUrl || undefined} />
                                    <AvatarFallback className="bg-amber-500 text-white text-xs">
                                        {getInitials(project.owner.name)}
                                    </AvatarFallback>
                                </Avatar>
                                {project.members.slice(0, 3).map((member) => (
                                    <Avatar
                                        key={member.id}
                                        className="h-8 w-8 border-2 border-surface"
                                    >
                                        <AvatarImage src={member.user.avatarUrl || undefined} />
                                        <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                                            {getInitials(member.user.name)}
                                        </AvatarFallback>
                                    </Avatar>
                                ))}
                                {project.memberCount > 4 && (
                                    <div className="h-8 w-8 rounded-full bg-surface-hover border-2 border-surface flex items-center justify-center text-xs text-foreground-muted">
                                        +{project.memberCount - 4}
                                    </div>
                                )}
                            </div>
                            {canManage && (
                                <>
                                    <Button variant="outline" size="sm" asChild className="hidden sm:flex">
                                        <Link to={`/projects/${project.id}/settings`}>
                                            <Users className="h-4 w-4 mr-2" />
                                            {t('projects.manageTeam')}
                                        </Link>
                                    </Button>
                                    <Button variant="outline" size="icon" asChild>
                                        <Link to={`/projects/${project.id}/settings`}>
                                            <Settings className="h-4 w-4" />
                                        </Link>
                                    </Button>
                                </>
                            )}
                        </div>
                    </div>
                </header>

                {/* Tasks area */}
                <div className={cn("flex-1 min-h-0 p-4 sm:p-6", viewType === 'CHAT' ? "flex flex-col overflow-hidden" : "overflow-auto")}>
                    {/* Tasks header */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 shrink-0">
                        <div className="flex flex-col sm:flex-row sm:items-center min-w-0 gap-3 sm:gap-4">
                            <h2 className="text-lg font-semibold text-foreground">{viewType === 'CHAT' ? 'Chat AI' : t('tasks.title')}</h2>
                            <span className={cn("text-sm text-foreground-muted", viewType === 'CHAT' && 'hidden')}>
                                {tasksData?.total ?? 0} {t('tasks.task')}{(tasksData?.total ?? 0) !== 1 ? 's' : ''}
                            </span>
                            {/* Table / Kanban toggle. Defaults to Table per
                                the team plan. The choice persists per
                                project via localStorage so reload keeps
                                the user where they were. */}
                            <div
                                role="tablist"
                                aria-label={t('viewType.toggleAriaLabel')}
                                className="inline-flex items-center max-w-full overflow-x-auto whitespace-nowrap shrink-0 rounded-md border border-white/10 bg-white/5 p-0.5"
                            >
                                <button
                                    role="tab"
                                    aria-selected={viewType === 'TABLE'}
                                    onClick={() => handleViewTypeChange('TABLE')}
                                    className={cn(
                                        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium transition-colors',
                                        viewType === 'TABLE'
                                            ? 'bg-primary text-primary-foreground shadow-sm'
                                            : 'text-foreground-muted hover:text-foreground'
                                    )}
                                >
                                    <LayoutGrid className="h-3.5 w-3.5" />
                                    {t('viewType.table')}
                                </button>
                                <button
                                    role="tab"
                                    aria-selected={viewType === 'KANBAN'}
                                    onClick={() => handleViewTypeChange('KANBAN')}
                                    className={cn(
                                        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium transition-colors',
                                        viewType === 'KANBAN'
                                            ? 'bg-primary text-primary-foreground shadow-sm'
                                            : 'text-foreground-muted hover:text-foreground'
                                    )}
                                >
                                    <Columns3 className="h-3.5 w-3.5" />
                                    {t('viewType.kanban')}
                                </button>
                                {/* t15: Calendar + Gantt tabs. Stub views
                                    render "coming soon" until t16 / t17
                                    ship their real implementations. */}
                                <button
                                    role="tab"
                                    aria-selected={viewType === 'CALENDAR'}
                                    onClick={() => handleViewTypeChange('CALENDAR')}
                                    className={cn(
                                        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium transition-colors',
                                        viewType === 'CALENDAR'
                                            ? 'bg-primary text-primary-foreground shadow-sm'
                                            : 'text-foreground-muted hover:text-foreground'
                                    )}
                                >
                                    <CalendarIcon className="h-3.5 w-3.5" />
                                    {t('viewType.calendar')}
                                </button>
                                <button
                                    role="tab"
                                    aria-selected={viewType === 'GANTT'}
                                    onClick={() => handleViewTypeChange('GANTT')}
                                    className={cn(
                                        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium transition-colors',
                                        viewType === 'GANTT'
                                            ? 'bg-primary text-primary-foreground shadow-sm'
                                            : 'text-foreground-muted hover:text-foreground'
                                    )}
                                >
                                    <GanttIcon className="h-3.5 w-3.5" />
                                    {t('viewType.gantt')}
                                </button>
                                {/* R14: Document mode (Google-Docs-like
                                    rich text editor + Yjs collaboration). */}
                                <button
                                    role="tab"
                                    aria-selected={viewType === 'DOCUMENT'}
                                    onClick={() => handleViewTypeChange('DOCUMENT')}
                                    data-testid="viewType-document"
                                    className={cn(
                                        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium transition-colors',
                                        viewType === 'DOCUMENT'
                                            ? 'bg-primary text-primary-foreground shadow-sm'
                                            : 'text-foreground-muted hover:text-foreground'
                                    )}
                                >
                                    <DocumentIcon className="h-3.5 w-3.5" />
                                    {t('viewType.document')}
                                </button>
                                <button role="tab" aria-selected={viewType === 'CHAT'} onClick={() => handleViewTypeChange('CHAT')} data-testid="viewType-ai"
                                    className={cn('inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium transition-colors', viewType === 'CHAT' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-foreground-muted hover:text-foreground')}>Chat AI</button>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => refetchTasks()}
                                disabled={tasksLoading}
                            >
                                <RefreshCw className={`h-4 w-4 ${tasksLoading ? 'animate-spin' : ''}`} />
                            </Button>
                            {canEditTasks && viewType !== 'CHAT' && (
                                <Button onClick={handleCreateTask} className="w-full sm:w-auto">
                                    <Plus className="h-4 w-4 mr-2" />
                                    {t('tasks.addTask')}
                                </Button>
                            )}
                        </div>
                    </div>

                    {/* Filters — R15: hidden in Document mode. The
                        document editor doesn't operate on the task
                        list, so the filter bar would be a dead
                        control that wastes vertical space + invites
                        users to filter a view they're not looking
                        at. The bar still renders for TABLE / KANBAN
                        / CALENDAR / GANBAN (the task-driven modes). */}
                    {viewType !== 'DOCUMENT' && viewType !== 'CHAT' ? (
                        <div className="mb-6">
                            <TaskFiltersBar
                                filters={filters}
                                // t10: we no longer write the new filters
                                // into a frozen activeView object — local
                                // filter state updates immediately for the
                                // task query, and the activeView (now derived
                                // from savedViewsData) will only catch up when
                                // the user explicitly clicks "Save view
                                // changes" or picks another view.
                                onFiltersChange={(newFilters) => {
                                    setFilters(newFilters);
                                }}
                                members={allMembers}
                                customFields={customFields}
                                onSaveAsView={() => setSaveViewDialogOpen(true)}
                                onUpdateView={handleUpdateView}
                                hasActiveView={!!activeView}
                                onAddCustomField={canManage ? () => setCustomFieldDialogOpen(true) : undefined}
                            />
                        </div>
                    ) : null}

                    {/* Tasks table or empty state */}
                    {viewType === 'CHAT' ? <AIChat projectId={projectId || ''} conversationId={aiConversationId} onSelect={selectAiConversation} context={aiContext} initialPrompt={aiInitialPrompt} onConsumePrompt={() => setAiInitialPrompt({ nonce: 0, text: '' })} userId={authUser?.id || ''} canCreate={canEditTasks} canManage={canManage} onTask={setSelectedTaskId}
                        onView={async viewId => { try { const view=await apiGet<SavedView>(`/views/${viewId}`); if(view.projectId!==projectId)return; setAiDocumentProposal(null); handleViewTypeChange(view.type as ViewType); handleViewSelect(view); setViewsInitialized(true); } catch(error) { toast({title:t('common.error'),description:error instanceof Error?error.message:'View unavailable',variant:'destructive'}); } }}
                        onDocumentEdit={(viewId, proposal) => { setAiDocumentProposal(proposal); handleViewTypeChange('DOCUMENT'); setActiveViewId(viewId); setViewsInitialized(true); }} /> : tasksLoading ? (
                        <div className="space-y-3">
                            {[...Array(5)].map((_, i) => (
                                <div
                                    key={i}
                                    className="h-14 bg-surface-hover rounded-lg animate-pulse"
                                />
                            ))}
                        </div>
                    ) : (viewType === 'CALENDAR' || viewType === 'GANTT' || viewType === 'DOCUMENT') ? (
                        // t15: Calendar / Gantt stubs render a friendly
                        // placeholder. The branches below don't depend on
                        // tasksData — the stubs only need projectId +
                        // activeView to show the active view name.
                        //
                        // R14: DOCUMENT gets its own branch — it owns
                        // the entire viewport (tabs + TipTap editor +
                        // auto-save) so it must render BEFORE the
                        // tasksData.length > 0 short-circuit that the
                        // Table / Kanban branches share. Document mode
                        // intentionally ignores the task list.
                        viewType === 'CALENDAR' ? (
                            <CalendarView
                                projectId={projectId || ''}
                                tasks={tasksData?.tasks || []}
                                isLoading={tasksLoading}
                                customFields={customFields}
                                activeView={activeView}
                                onTaskClick={handleTaskClick}
                                onCreateTaskAtDate={canEditTasks ? handleCreateTaskAtDate : undefined}
                                onDeleteTask={canEditTasks ? handleDeleteTask : undefined}
                                canEdit={canEditTasks}
                            />
                        ) : viewType === 'GANTT' ? (
                            // t17: GanttView now accepts the same
                            // prop shape as CalendarView + KanbanView
                            // — full task list, customFields, the
                            // active saved view, click handler and
                            // canEdit flag. Drag-to-reschedule lives
                            // inside the view (dnd-kit).
                            <GanttView
                                projectId={projectId || ''}
                                tasks={tasksData?.tasks || []}
                                isLoading={tasksLoading}
                                customFields={customFields}
                                activeView={activeView}
                                onTaskClick={handleTaskClick}
                                canEdit={canEditTasks}
                            />
                        ) : (
                            // R14: Document mode — TipTap rich text +
                            // Yjs collab. Reads user info from the
                            // current project context (we don't need
                            // tasksData).
                            //
                            // R20 (Bug 1 fix): DocumentView now receives
                            // `activeView` as a prop (mirroring
                            // CalendarView + GanttView). Previously the
                            // saved-views sidebar's `onViewSelect`
                            // updated the OUTER activeViewId in
                            // project-detail.tsx but DocumentView kept
                            // its own internal copy that was never
                            // refreshed — every view showed the same
                            // content + cursor sync.
                            <DocumentView
                                aiProposal={aiDocumentProposal}
                                onDismissAiProposal={() => setAiDocumentProposal(null)}
                                projectId={projectId || ''}
                                activeView={activeView}
                                // R24: the parent owns `activeViewId`, so
                                // DocumentView asks it to open a freshly
                                // created document (the empty-state CTA
                                // used to leave the user on the empty
                                // state after creating the view).
                                onSelectView={setActiveViewId}
                                activeUser={{
                                    id: authUser?.id || '',
                                    name: authUser?.name || t('common.unassigned'),
                                }}
                                canEdit={canEditTasks}
                                // R14: clicking a mention chip in the
                                // document opens the task-detail side
                                // panel. We delegate via onOpenTask
                                // (rather than calling setSelectedTaskId
                                // directly) to keep the document editor
                                // decoupled from page-level navigation.
                                onOpenTask={(taskId) => setSelectedTaskId(taskId)}
                            />
                        )
                    ) : tasksData?.tasks && tasksData.tasks.length > 0 ? (
                        <div className="space-y-3">
                            {viewType === 'KANBAN' ? (
                                <KanbanView
                                    projectId={projectId || ''}
                                    tasks={tasksData.tasks}
                                    isLoading={tasksLoading}
                                    customFields={customFields}
                                    visibleCardFieldIds={activeView?.visibleCardFields || []}
                                    activeView={viewType === 'KANBAN' ? activeView : null}
                                    // t10: re-key activeViewId on the auto-clone fork.
                                    onActiveViewIdChange={setActiveViewId}
                                    onCardClick={handleTaskClick}
                                    // t15: clicking a subtask in the
                                    // card's popover opens the existing
                                    // task-detail panel for that subtask.
                                    onSelectSubtask={handleTaskClick}
                                    onTaskMoved={canEditTasks ? handleKanbanTaskMoved : undefined}
                                    onClearFilters={() => setFilters({})}
                                    hasActiveFilters={Object.keys(filters).length > 0}
                                    canEdit={canEditTasks}
                                    projectMembers={allMembers}
                                />
                            ) : (
                                <>
                            <TaskTable
                                tasks={tasksData.tasks}
                                projectId={projectId || ''}
                                onEditTask={handleEditTask}
                                onTaskClick={handleTaskClick}
                                onDeleteTask={handleDeleteTask}
                                onStatusChange={handleStatusChange}
                                onPriorityChange={handlePriorityChange}
                                selectedTasks={selectedTasks}
                                onSelectionChange={setSelectedTasks}
                                onInlineCreate={canEditTasks ? handleInlineCreate : undefined}
                                canEdit={canEditTasks}
                                columnOrder={columnOrder}
                                hiddenColumns={hiddenColumns}
                                onColumnOrderChange={(newOrder) => {
                                    // t10: column order is local UI state.
                                    // Don't write into a frozen activeView
                                    // object — let the user explicitly save
                                    // via "Save view changes" when they
                                    // want the order persisted.
                                    setColumnOrder(newOrder);
                                }}
                                onHiddenColumnsChange={(newHidden) => {
                                    // t10: same — local UI state only.
                                    setHiddenColumns(newHidden);
                                }}
                                customFields={customFields}
                                projectMembers={allMembers}
                                onAssigneeChange={canEditTasks ? handleAssigneeChange : undefined}
                                onDueDateChange={canEditTasks ? handleDueDateChange : undefined}
                                onCustomFieldChange={canEditTasks ? handleCustomFieldChange : undefined}
                                onEditCustomField={canManage ? handleEditCustomField : undefined}
                                onDeleteCustomField={canManage ? handleDeleteCustomField : undefined}
                                onQuickCreate={canEditTasks ? handleQuickCreate : undefined}
                                onTitleChange={canEditTasks ? handleTitleChange : undefined}
                                onBulkDelete={canEditTasks ? handleBulkDelete : undefined}
                                onBulkStatusChange={canEditTasks ? handleBulkStatusChange : undefined}
                                onBulkPriorityChange={canEditTasks ? handleBulkPriorityChange : undefined}
                                onBulkAssigneeChange={canEditTasks ? handleBulkAssigneeChange : undefined}
                                onBulkCustomFieldChange={canEditTasks ? handleBulkCustomFieldChange : undefined}
                                filterDefaults={filterDefaults}
                            />
                            <div className="flex flex-col items-center justify-center gap-2 py-2 sm:flex-row">
                                <span className="text-xs text-foreground-muted">
                                    {t('tasks.paginationShowing')} {tasksData.tasks.length} {t('tasks.paginationOf')} {tasksData.total}{' '}
                                    {t('tasks.task')}{tasksData.total !== 1 ? 's' : ''}
                                </span>
                                {hasNextPage && (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => fetchNextPage()}
                                        disabled={isFetchingNextPage}
                                    >
                                        {isFetchingNextPage && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                        {isFetchingNextPage ? t('common.loading') : t('tasks.loadMore')}
                                    </Button>
                                )}
                            </div>
                                </>
                            )}
                        </div>
                    ) : viewType === 'KANBAN' ? (
                        // Empty Kanban: KanbanView renders its own empty
                        // state so users see all five columns with a
                        // "Drop a card here" hint instead of the table's
                        // inline-create row.
                        <KanbanView
                            projectId={projectId || ''}
                            tasks={tasksData?.tasks || []}
                            isLoading={tasksLoading}
                            customFields={customFields}
                            visibleCardFieldIds={activeView?.visibleCardFields || []}
                            activeView={viewType === 'KANBAN' ? activeView : null}
                            onActiveViewIdChange={setActiveViewId}
                            onCardClick={handleTaskClick}
                            // t15: subtask popover callback
                            onSelectSubtask={handleTaskClick}
                            onTaskMoved={canEditTasks ? handleKanbanTaskMoved : undefined}
                            onClearFilters={() => setFilters({})}
                            hasActiveFilters={Object.keys(filters).length > 0}
                            canEdit={canEditTasks}
                            projectMembers={allMembers}
                        />
                    ) : (
                        // Empty Table branch. CALENDAR and GANTT stubs are
                        // handled above (before the has-tasks check) so by
                        // here viewType is narrowed to 'TABLE' | 'KANBAN',
                        // and the KANBAN case is already taken above.
                        <div className="space-y-4">
                            <div className="flex flex-col items-center justify-center py-16 text-center">
                                <div className="h-16 w-16 rounded-full bg-surface-hover flex items-center justify-center mb-4">
                                    <CheckSquare className="h-8 w-8 text-foreground-muted" />
                                </div>
                                <h3 className="text-lg font-medium text-foreground mb-2">
                                    {Object.keys(filters).length > 0 ? t('tasks.noMatchingTasks') : t('tasks.noTasks')}
                                </h3>
                                <p className="text-foreground-muted mb-4 max-w-sm">
                                    {Object.keys(filters).length > 0
                                        ? t('tasks.tryAdjustingFilters')
                                        : t('tasks.getStartedCreatingTask')}
                                </p>
                                {canEditTasks && Object.keys(filters).length === 0 && (
                                    <Button onClick={handleCreateTask}>
                                        <Plus className="h-4 w-4 mr-2" />
                                        {t('tasks.createFirstTask')}
                                    </Button>
                                )}
                            </div>
                            {/* Show inline add row even when no tasks */}
                            {canEditTasks && (
                                <TaskTable
                                    tasks={[]}
                                    projectId={projectId || ''}
                                    onEditTask={() => { }}
                                    onTaskClick={() => { }}
                                    onDeleteTask={() => { }}
                                    onStatusChange={() => { }}
                                    onPriorityChange={() => { }}
                                    onInlineCreate={handleInlineCreate}
                                    onQuickCreate={handleQuickCreate}
                                    onTitleChange={handleTitleChange}
                                    canEdit={true}
                                    columnOrder={columnOrder}
                                    hiddenColumns={hiddenColumns}
                                    onColumnOrderChange={setColumnOrder}
                                    onHiddenColumnsChange={setHiddenColumns}
                                    customFields={customFields}
                                    projectMembers={allMembers}
                                    onEditCustomField={canManage ? handleEditCustomField : undefined}
                                    onDeleteCustomField={canManage ? handleDeleteCustomField : undefined}
                                    filterDefaults={filterDefaults}
                                />
                            )}
                        </div>
                    )}
                </div>
            </main>

            {projectId && <AiHistoryDialog projectId={projectId} open={mcpActivityOpen} onClose={() => { setMcpActivityOpen(false); const url = new URL(window.location.href); url.searchParams.delete('aiActivity'); window.history.replaceState({}, '', url); }} />}

            {/* Task Dialog */}
            {project && (
                <TaskDialog
                    open={taskDialogOpen}
                    onOpenChange={setTaskDialogOpen}
                    task={editingTask}
                    projectMembers={project.members}
                    projectOwnerId={project.owner.id}
                    projectOwner={project.owner}
                    onSubmit={handleTaskSubmit}
                    isLoading={createTask.isPending}
                    customFields={customFields}
                    initialDueDate={taskDialogInitialDueDate}
                />
            )}

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{t('tasks.deleteTask')}</DialogTitle>
                        <DialogDescription>
                            {t('confirm.deleteTask')}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setDeleteDialogOpen(false)}
                            disabled={deleteTask.isPending}
                        >
                            {t('common.cancel')}
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={confirmDeleteTask}
                            disabled={deleteTask.isPending}
                        >
                            {deleteTask.isPending && (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            )}
                            {t('common.delete')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Task Detail Panel */}
            {selectedTaskId && (
                <TaskDetailPanel
                    canEdit={canEditTasks}
                    taskId={selectedTaskId}
                    projectId={projectId || ''}
                    onAskAi={canEditTasks ? task => { handleViewTypeChange('CHAT'); selectAiConversation(null); setAiContext(current => ({ ...current, selectedTaskIds: [task.id] })); setAiInitialPrompt({ nonce: Date.now(), text: language === 'fr' ? `Analyse @[${task.title}](task:${task.id}), ses blocages et propose des sous-tâches.` : `Analyze @[${task.title}](task:${task.id}), its blockers and propose subtasks.` }); handleCloseTaskDetail(); } : undefined}
                    onClose={handleCloseTaskDetail}
                    onEdit={handleEditTask}
                    onSubtaskSelect={handleTaskClick}
                />
            )}

            {/* Custom Field Creation Dialog */}
            {projectId && (
                <FieldFormDialog
                    open={customFieldDialogOpen}
                    onOpenChange={(open) => {
                        setCustomFieldDialogOpen(open);
                        if (!open) setEditingCustomField(null);
                    }}
                    projectId={projectId}
                    editingField={editingCustomField}
                />
            )}

            {/* Delete Custom Field Confirmation */}
            <Dialog open={!!deleteCustomFieldId} onOpenChange={(open) => !open && setDeleteCustomFieldId(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{t('customFields.deleteField')}</DialogTitle>
                        <DialogDescription>
                            {t('confirm.deleteCustomField')}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setDeleteCustomFieldId(null)}
                            disabled={deleteCustomField.isPending}
                        >
                            {t('common.cancel')}
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={confirmDeleteCustomField}
                            disabled={deleteCustomField.isPending}
                        >
                            {deleteCustomField.isPending && (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            )}
                            {t('common.delete')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* t15 Part B.3: Add Column dialog opened after creating a
                KANBAN view (or after the Kanban default seed). Mounted at
                page level so SavedViewsPanel can drive it without owning
                the dialog itself. */}
            <AddColumnDialog
                open={addColumnOpen}
                onOpenChange={setAddColumnOpen}
                onSubmit={async (data) => {
                    if (!activeView) return;
                    try {
                        await createColumnHook.mutateAsync(data);
                    } catch (err) {
                        console.error('Failed to create column:', err);
                    }
                }}
                customFields={customFields}
                projectMembers={allMembers}
            />
        </div>
    );
}
